import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  useMantineTheme,
  Paper,
  Badge,
  Group,
  ThemeIcon,
  Stack,
  Divider,
  ActionIcon
} from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { api } from '../../services/master.service';
import { showErrorToast } from '../../utilities/Toast';
import ViewOrderModal from '../../components/ViewOrderModal';
import moment from 'moment';
import { order } from '../../services/website.service';
import { openConfirmModal } from '@mantine/modals';
import { is_mobile_view } from '../../config';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconPackage,
  IconTruckDelivery,
  IconCircleCheck,
  IconX,
  IconEye,
  IconShoppingBag,
  IconClock
} from '@tabler/icons-react';
import { motion, AnimatePresence } from 'framer-motion';

function Orders() {
  let isMobileScreen = useMediaQuery('(max-width: 768px)');
  isMobileScreen = is_mobile_view ? is_mobile_view : isMobileScreen;
  const theme = useMantineTheme();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewModalOpened, setViewModalOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = () => {
    setIsLoading(true);
    api.get_orders().then((res) => {
      setOrders(res.data.data || []);
      setIsLoading(false);
    }).catch(() => setIsLoading(false));
  };

  const getStatusConfig = (status) => {
    switch (status.toLowerCase()) {
      case 'pending': return { color: 'orange', icon: IconClock, label: 'Pending' };
      case 'accepted': return { color: 'blue', icon: IconPackage, label: 'Accepted' };
      case 'packed': return { color: 'yellow', icon: IconPackage, label: 'Packed' };
      case 'out_for_delivery': return { color: 'indigo', icon: IconTruckDelivery, label: 'Out for Delivery' };
      case 'delivered': return { color: 'green', icon: IconCircleCheck, label: 'Delivered' };
      case 'cancelled': return { color: 'red', icon: IconX, label: 'Cancelled' };
      default: return { color: 'gray', icon: IconPackage, label: status };
    }
  };

  const handleCancelOrder = (id) => {
    openConfirmModal({
      title: 'Cancel Order',
      centered: true,
      children: (
        <Text size="sm">Are you sure you want to cancel this order? This action cannot be undone.</Text>
      ),
      labels: { confirm: 'Yes, Cancel', cancel: 'No, Keep' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          const res = await order.cancel(id);
          if (res.success) {
            fetchOrders();
          }
        } catch (err) {
          showErrorToast({ title: 'Error', message: 'Could not cancel order' });
        }
      },
    });
  };

  if (!isLoading && orders.length === 0) {
    return (
      <Paper
        radius={24}
        p={isMobileScreen ? 28 : 44}
        mt={10}
        sx={{
          textAlign: 'center',
          background: 'linear-gradient(145deg, rgba(255,255,255,.98), rgba(248,250,252,.92))',
          border: `1px solid ${theme.colors.gray[2]}`,
          boxShadow: '0 18px 45px rgba(15, 23, 42, 0.06)',
        }}
      >
        <ThemeIcon size={84} radius={24} variant="light" color="green" mb={20} mx="auto">
          <IconShoppingBag size={40} />
        </ThemeIcon>
        <Text fw={900} size={isMobileScreen ? 22 : 26} color="dark">No Orders Yet</Text>
        <Text color="dimmed" size="sm" mt={8}>You haven't placed any orders yet.</Text>
        <Button radius="xl" variant="light" color="green" mt={24} onClick={() => window.location.href = '/'}>
          Start Shopping
        </Button>
      </Paper>
    );
  }

  return (
    <Box py={isMobileScreen ? 10 : 18}>
      <Paper
        radius={22}
        p={isMobileScreen ? 16 : 22}
        mb={20}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${theme.colors.green[0]}, ${theme.white} 54%, ${theme.colors.gray[0]})`,
          border: `1px solid ${theme.colors.green[1]}`,
          boxShadow: '0 18px 42px rgba(13, 71, 21, 0.08)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            right: -48,
            top: -72,
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(34,197,94,.18), transparent 68%)',
          }}
        />
        <Flex
          justify="space-between"
          align={isMobileScreen ? 'flex-start' : 'center'}
          direction={isMobileScreen ? 'column' : 'row'}
          gap={12}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Group spacing={12} noWrap>
            <ThemeIcon
              size={52}
              radius={16}
              sx={{
                background: `linear-gradient(135deg, ${theme.colors.brand[7]}, ${theme.colors.brand[5]})`,
                boxShadow: '0 12px 24px rgba(13, 71, 21, 0.18)',
              }}
            >
              <IconPackage size={26} />
            </ThemeIcon>
            <Box>
              <Text fw={900} size={isMobileScreen ? 22 : 26} color="dark" lh={1.1}>
                My Orders
              </Text>
              <Text size="sm" color="dimmed" mt={4}>
                Track your purchases and delivery progress.
              </Text>
            </Box>
          </Group>
          <Badge
            radius="xl"
            size="lg"
            variant="light"
            color="green"
            sx={{ fontWeight: 900, textTransform: 'none' }}
          >
            {orders.length} Order{orders.length > 1 ? 's' : ''}
          </Badge>
        </Flex>
      </Paper>

      <Grid gutter={isMobileScreen ? 14 : 18}>
        <AnimatePresence>
          {orders.map((e, idx) => {
            const statusCfg = getStatusConfig(e.status);
            return (
              <Grid.Col span={12} md={6} key={e.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Paper
                    p={0}
                    radius={22}
                    withBorder
                    sx={{
                      height: '100%',
                      overflow: 'hidden',
                      background: 'linear-gradient(145deg, rgba(255,255,255,.99), rgba(248,250,252,.95))',
                      borderColor: theme.colors.gray[2],
                      boxShadow: '0 14px 34px rgba(15, 23, 42, 0.06)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        borderColor: theme.colors.green[2],
                        boxShadow: '0 20px 44px rgba(13, 71, 21, 0.10)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 5,
                        background: `linear-gradient(90deg, ${theme.colors.brand[7]}, ${theme.colors.brand[4]}, ${theme.colors.green[1]})`,
                      }}
                    />

                    <Stack spacing={14} p={isMobileScreen ? 14 : 18}>
                      <Flex justify="space-between" align="flex-start" gap={14}>
                        <Group spacing={10} align="flex-start" noWrap>
                          <ThemeIcon size={42} radius={14} variant="light" color={statusCfg.color}>
                            <statusCfg.icon size={21} />
                          </ThemeIcon>
                          <Box>
                            <Text fw={900} size="sm" color="dark">#{e.order_id}</Text>
                            <Text size={11} color="dimmed" mt={4}>
                              Placed on {moment(e.timestamp).format('MMM DD, YYYY')} at {moment(e.timestamp).format('hh:mm A')}
                            </Text>
                          </Box>
                        </Group>

                        <Box ta="right" sx={{ flex: '0 0 auto' }}>
                          <Text fw={900} size={isMobileScreen ? 18 : 20} color="dark">
                            &#8377;{e.total}
                          </Text>
                          <Badge
                            mt={5}
                            size="sm"
                            color={statusCfg.color}
                            variant="light"
                            leftSection={<statusCfg.icon size={12} />}
                            sx={{ textTransform: 'none', fontWeight: 800 }}
                          >
                            {statusCfg.label}
                          </Badge>
                        </Box>
                      </Flex>

                      <Divider variant="dashed" />

                      <Flex justify="space-between" align="center" gap={12} wrap="wrap">
                        <Group spacing={8}>
                          <ThemeIcon size={30} radius="xl" variant="light" color="gray">
                            <IconShoppingBag size={16} />
                          </ThemeIcon>
                          <Text size="xs" color="dimmed" fw={700}>
                            {e.order_items_count || 1} Item{ (e.order_items_count || 1) > 1 ? 's' : ''}
                          </Text>
                        </Group>

                        <Group spacing={8}>
                          {e.status.toLowerCase() === 'accepted' && (
                            <Button
                              variant="subtle"
                              color="red"
                              size="xs"
                              compact
                              radius="xl"
                              onClick={() => handleCancelOrder(e.id)}
                            >
                              Cancel
                            </Button>
                          )}
                          {isMobileScreen ? (
                            <ActionIcon
                              variant="light"
                              color="green"
                              radius="xl"
                              size={34}
                              onClick={() => {
                                setSelectedOrder(e.id);
                                setViewModalOpened(true);
                              }}
                            >
                              <IconEye size={17} />
                            </ActionIcon>
                          ) : (
                            <Button
                              onClick={() => {
                                setSelectedOrder(e.id);
                                setViewModalOpened(true);
                              }}
                              variant="light"
                              color="green"
                              size="xs"
                              leftIcon={<IconEye size={14} />}
                              radius="xl"
                            >
                              View Details
                            </Button>
                          )}
                        </Group>
                      </Flex>
                    </Stack>
                  </Paper>
                </motion.div>
              </Grid.Col>
            );
          })}
        </AnimatePresence>
      </Grid>

      <ViewOrderModal
        orderId={selectedOrder}
        opened={viewModalOpened}
        onClose={() => setViewModalOpened(false)}
      />
    </Box>
  );
}

export default Orders;
